import React, { useEffect, useState } from "react";
import { Container } from "./styles";
import api from "../../../services/api";
import { format, subDays } from "date-fns";
import { DatePicker, Spin, Table, Button, Tag } from "antd";
import {
  MdAttachMoney,
  MdPeopleOutline,
  MdTrendingUp,
  MdBarChart,
  MdStarOutline,
  MdPieChartOutline,
  MdArrowBack,
} from "react-icons/md";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { getValue } from "../../../services/functions";
import { ViewInput, Scroll } from "../../../GlobalStyles";
import { toast } from "react-toastify";

const { RangePicker } = DatePicker;

const statusesList = [
  "Pendente",
  "Oportunidade",
  "Abordagem",
  "Atendimento",
  "Qualificação",
  "Proposta Enviada",
  "Negociação",
  "Cliente Perdido",
  "Cliente Fechado",
];

const statusColors = {
  Pendente: "#999",
  Oportunidade: "#1890ff",
  Abordagem: "#722ed1",
  Atendimento: "#eb2f96",
  Qualificação: "#2f54eb",
  "Proposta Enviada": "#fa8c16",
  Negociação: "#faad14",
  "Cliente Perdido": "#f5222d",
  "Cliente Fechado": "#008037",
};

export default function AssociadosRelatorioGeral() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dateStart, setDateStart] = useState(subDays(new Date(), 30));
  const [dateEnd, setDateEnd] = useState(new Date());
  const [associatesData, setAssociatesData] = useState([]);
  const [totals, setTotals] = useState({
    leads: 0,
    earnings: 0,
    potential: 0,
  });

  useEffect(() => {
    loadGlobalData();
  }, [dateStart, dateEnd]);

  async function loadGlobalData() {
    setLoading(true);
    try {
      const response = await api.get("/associates");
      const list = response.data.associates;

      const fullData = await Promise.all(
        list.map(async (associate) => {
          try {
            const resumeRes = await api.get(`/associado/resume`, {
              params: {
                user_id: associate.id,
                start_date: format(dateStart, "yyyy-MM-dd"),
                end_date: format(dateEnd, "yyyy-MM-dd"),
              },
            });
            return {
              ...associate,
              resume: resumeRes.data,
            };
          } catch (e) {
            return {
              ...associate,
              resume: {
                totalLeadsCreated: 0,
                totalLeadsClienteFechadoValue: 0,
                totalLeadsPotentialValue: 0,
                totalLeadsClienteFechado: 0,
                totalLeadsClientePerdido: 0,
              },
            };
          }
        }),
      );

      const globalTotals = fullData.reduce(
        (acc, curr) => {
          acc.leads += curr.resume.totalLeadsCreated || 0;
          acc.earnings += curr.resume.totalLeadsClienteFechadoValue || 0;
          acc.potential += curr.resume.totalLeadsPotentialValue || 0;
          return acc;
        },
        { leads: 0, earnings: 0, potential: 0 },
      );

      setTotals(globalTotals);
      setAssociatesData(fullData);
    } catch (error) {
      toast.error("Erro ao carregar dados globais");
    }
    setLoading(false);
  }

  const getRankings = () => {
    const byLeads = [...associatesData]
      .sort(
        (a, b) =>
          (b.resume.totalLeadsCreated || 0) - (a.resume.totalLeadsCreated || 0),
      )
      .slice(0, 5);

    const byEarnings = [...associatesData]
      .sort(
        (a, b) =>
          (b.resume.totalLeadsClienteFechadoValue || 0) -
          (a.resume.totalLeadsClienteFechadoValue || 0),
      )
      .slice(0, 5);

    const byConversion = [...associatesData]
      .map((a) => {
        const closed = a.resume.totalLeadsClienteFechado || 0;
        const lost = a.resume.totalLeadsClientePerdido || 0;
        const total = closed + lost;
        return {
          ...a,
          conversionRate: total > 0 ? (closed / total) * 100 : 0,
        };
      })
      .sort((a, b) => b.conversionRate - a.conversionRate)
      .slice(0, 5);

    return { byLeads, byEarnings, byConversion };
  };

  const rankings = getRankings();

  const columns = [
    {
      title: "Associado",
      dataIndex: "name",
      key: "name",
      fixed: "left",
      width: 200,
      render: (text, record) => (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            cursor: "pointer",
          }}
          onClick={() => navigate(`/admin/associado/${record.id}`)}
        >
          <img
            src={record.photo_url || "/user.png"}
            style={{
              width: 30,
              height: 30,
              borderRadius: "50%",
              objectFit: "cover",
            }}
            alt=""
          />
          <div style={{ display: "flex", flexDirection: "column" }}>
            <strong style={{ fontSize: 13 }}>{text}</strong>
            <span style={{ fontSize: 11, color: "#999" }}>{record.email}</span>
          </div>
        </div>
      ),
    },
    {
      title: "Resumo Financeiro",
      children: [
        {
          title: "Faturamento",
          dataIndex: ["resume", "totalLeadsClienteFechadoValue"],
          key: "earnings",
          width: 130,
          render: (val) => (
            <span style={{ fontWeight: "bold", color: "#008037" }}>
              {getValue(val || 0)}
            </span>
          ),
          sorter: (a, b) =>
            (a.resume.totalLeadsClienteFechadoValue || 0) -
            (b.resume.totalLeadsClienteFechadoValue || 0),
        },
        {
          title: "Em Negociação",
          dataIndex: ["resume", "totalLeadsPotentialValue"],
          key: "potential",
          width: 130,
          render: (val) => (
            <span style={{ color: "#f2760a" }}>{getValue(val || 0)}</span>
          ),
          sorter: (a, b) =>
            (a.resume.totalLeadsPotentialValue || 0) -
            (b.resume.totalLeadsPotentialValue || 0),
        },
      ],
    },
    {
      title: "Funil de Leads (Status)",
      children: statusesList.map((status) => ({
        title: status,
        dataIndex: [
          "resume",
          `totalLeads${status
            .replace(/\s/g, "")
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "")}`,
        ],
        key: status,
        width: 110,
        align: "center",
        render: (val) => val || 0,
        sorter: (a, b) => {
          const key = `totalLeads${status
            .replace(/\s/g, "")
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "")}`;
          return (a.resume[key] || 0) - (b.resume[key] || 0);
        },
      })),
    },
    {
      title: "Performance",
      children: [
        {
          title: "Total Leads",
          dataIndex: ["resume", "totalLeadsCreated"],
          key: "leads_total",
          width: 110,
          align: "center",
          sorter: (a, b) =>
            (a.resume.totalLeadsCreated || 0) -
            (b.resume.totalLeadsCreated || 0),
        },
        {
          title: "Conv. %",
          key: "conversion",
          width: 100,
          align: "center",
          render: (_, record) => {
            const closed = record.resume.totalLeadsClienteFechado || 0;
            const lost = record.resume.totalLeadsClientePerdido || 0;
            const total = closed + lost;
            const rate =
              total > 0 ? ((closed / total) * 100).toFixed(1) : "0.0";
            return (
              <Tag color={parseFloat(rate) > 50 ? "green" : "orange"}>
                {rate}%
              </Tag>
            );
          },
          sorter: (a, b) => {
            const rateA =
              a.resume.totalLeadsClienteFechado /
                (a.resume.totalLeadsClienteFechado +
                  a.resume.totalLeadsClientePerdido) || 0;
            const rateB =
              b.resume.totalLeadsClienteFechado /
                (b.resume.totalLeadsClienteFechado +
                  b.resume.totalLeadsClientePerdido) || 0;
            return rateA - rateB;
          },
        },
      ],
    },
  ];

  return (
    <Container>
      <div className="dashboard-header">
        <div style={{ display: "flex", alignItems: "center", gap: 15 }}>
          <Button
            shape="circle"
            icon={<MdArrowBack size={20} />}
            onClick={() => navigate("/admin/associados")}
          />
          <h1>Relatório Geral de Associados</h1>
        </div>
        <ViewInput style={{ width: 280 }}>
          <p>Filtrar Período Global</p>
          <RangePicker
            style={{ width: "100%" }}
            value={[dayjs(dateStart), dayjs(dateEnd)]}
            format={"DD/MM/YYYY"}
            onChange={(dates) => {
              if (dates) {
                setDateStart(dates[0].toDate());
                setDateEnd(dates[1].toDate());
              }
            }}
            allowClear={false}
          />
        </ViewInput>
      </div>

      {loading ? (
        <div
          style={{ display: "flex", justifyContent: "center", padding: 100 }}
        >
          <Spin size="large" color="#008037" />
        </div>
      ) : (
        <>
          <div className="main-tags">
            <div className="tag-item highlight">
              <div className="tag-header">
                <MdAttachMoney size={20} /> Comissões Totais Pagas
              </div>
              <span className="value">{getValue(totals.earnings)}</span>
            </div>
            <div className="tag-item blue">
              <div className="tag-header">
                <MdPeopleOutline size={20} /> Total de Leads Gerados
              </div>
              <span className="value">{totals.leads}</span>
            </div>
            <div className="tag-item warning">
              <div className="tag-header">
                <MdTrendingUp size={20} /> Valor Total em Negociação
              </div>
              <span className="value">{getValue(totals.potential)}</span>
            </div>
          </div>

          <h2 className="section-title">Rankings de Performance (Top 5)</h2>
          <div className="rankings-grid">
            <div className="ranking-card">
              <h3>
                <MdBarChart size={22} color="#1890ff" /> Volume de Leads
              </h3>
              <div className="ranking-list">
                {rankings.byLeads.map((item, i) => (
                  <div className="ranking-item" key={item.id}>
                    <div className={`position pos-${i + 1}`}>{i + 1}</div>
                    <div className="associate-info">
                      <strong>{item.name}</strong>
                      <span>{item.email}</span>
                    </div>
                    <div className="score">
                      {item.resume.totalLeadsCreated || 0}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="ranking-card">
              <h3>
                <MdStarOutline size={22} color="#ffd700" /> Faturamento Gerado
              </h3>
              <div className="ranking-list">
                {rankings.byEarnings.map((item, i) => (
                  <div className="ranking-item" key={item.id}>
                    <div className={`position pos-${i + 1}`}>{i + 1}</div>
                    <div className="associate-info">
                      <strong>{item.name}</strong>
                      <span>{item.email}</span>
                    </div>
                    <div className="score">
                      {getValue(item.resume.totalLeadsClienteFechadoValue || 0)}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="ranking-card">
              <h3>
                <MdPieChartOutline size={22} color="#008037" /> Taxa de
                Conversão
              </h3>
              <div className="ranking-list">
                {rankings.byConversion.map((item, i) => (
                  <div className="ranking-item" key={item.id}>
                    <div className={`position pos-${i + 1}`}>{i + 1}</div>
                    <div className="associate-info">
                      <strong>{item.name}</strong>
                      <span>{item.email}</span>
                    </div>
                    <div className="score">
                      {(item.conversionRate || 0).toFixed(1)}%
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <h2 className="section-title">Análise Detalhada por Associado</h2>
          <div className="table-container">
            <Scroll>
              <Table
                dataSource={associatesData}
                columns={columns}
                rowKey="id"
                pagination={false}
                bordered
                size="small"
                scroll={{ x: 1500 }}
              />
            </Scroll>
          </div>
        </>
      )}
    </Container>
  );
}
